import type { FlowPartnerKind, PortalScope, PrinterPortalFilters } from "../types";
import type { BoardWindow } from "../../SupplierOrders/lib/boardWindow";
import { scopeKey } from "../lib/scope";

/**
 * `["partner-portal", partner, scopeKey, ...]` (frontend spec 7.1). The own
 * login and the team's view-as never share an entry; within a scope keys run
 * general → specific so a write can invalidate exactly what it touched.
 */
const base = (scope: PortalScope) => ["partner-portal", scope.partner, scopeKey(scope)] as const;

export const partnerPortalKeys = {
  all: ["partner-portal"] as const,
  partner: (partner: FlowPartnerKind) => ["partner-portal", partner] as const,
  /** Keyed by user id so a second login of the same kind on this tab never reads the first's session. */
  session: (partner: FlowPartnerKind, userId: number) =>
    ["partner-portal", partner, "own", "session", userId] as const,
  scope: base,
  bootstrap: (scope: PortalScope) => [...base(scope), "bootstrap"] as const,
  summaries: (scope: PortalScope) => [...base(scope), "summary"] as const,
  summary: (scope: PortalScope, window: BoardWindow) => [...base(scope), "summary", window] as const,
  countsAll: (scope: PortalScope) => [...base(scope), "counts"] as const,
  counts: (scope: PortalScope, filters: PrinterPortalFilters) => [...base(scope), "counts", filters] as const,
  columns: (scope: PortalScope) => [...base(scope), "column"] as const,
  column: (scope: PortalScope, lane: string, filters: PrinterPortalFilters, pageSize: number) =>
    [...base(scope), "column", lane, filters, pageSize] as const,
  schedules: (scope: PortalScope) => [...base(scope), "schedule"] as const,
  schedule: (scope: PortalScope, from: string, to: string) => [...base(scope), "schedule", from, to] as const,
  orders: (scope: PortalScope) => [...base(scope), "order"] as const,
  order: (scope: PortalScope, id: string) => [...base(scope), "order", id] as const,
  files: (scope: PortalScope, id: string) => [...base(scope), "files", id] as const,
};

/** The lane inside a `column` key. */
export function laneOfKey(key: readonly unknown[]): string | null {
  return key[3] === "column" && typeof key[4] === "string" ? key[4] : null;
}
